import FieldStrategy from '../FieldStrategy';
import { generateFieldName } from '@/views/fusion/investment/intelligence-create/tools';
import { checkboxFilterNone, transformSelectedTimeToTimeSeries } from '@/tools/form';
import { generateHtmlContent } from '@/views/fusion/investment/intelligence-create/tools/form';
import {
  CAMPAIGN_MODULE_KEY,
  CREATIVE_MODULE_KEY,
  CREATIVE_CONTENT_FORM_KEY,
  CREATIVE_COMPONENTS_FORM_KEY,
  TARGETING_MODULE_KEY,
  MATERIAL_MODULE_KEY,
  TITLE_MODULE_KEY,
  SITE_MODULE_KEY,
  CAMPAIGN_DELIVERY_FORM_KEY,
} from '@/views/fusion/investment/intelligence-create/strategy/constants';
import { OPTIONS_TYPE, CHANNEL_INTERFACE_TYPE, TIME_PERIOD_LENGTH } from '@/enums';
import calculator from '@/utils/calculator';

const getNumberOptions = (min, max, disabled_num = 999) => {
  const options = [];
  for (let i = min; i <= max; i++) {
    options.push({ label: i, value: i, disabled: i >= disabled_num });
  }
  return options;
};

/**
 * 获取设备价格选项
 * @returns {Object[]}
 */
const getLaunchPriceOptions = () => {
  const price_range = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000, 6000, 7000, 8000, 9000, 10000];
  return price_range.map(price => ({ label: `${price}元`, value: price }));
};

const isHarmonyCpcOrCpm = (delivery_platform_type, price_type) => {
  return delivery_platform_type === 'HARMONY' && ['PRICING_CPC', 'PRICING_CPM'].includes(price_type);
};

/**
 * 头条智擎版策略类
 */
export default class JrttV3FieldStrategy extends FieldStrategy {
  /**
   * 获取差异化模块字段
   * @returns {Object}
   */
  getDifferentiatedFields() {
    return {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              delivery_platform_type: {
                value: 'ANDROID',
                backup_value: 'ANDROID',
              },
              delivery_content_id: {
                type: '',
                is_single_advertiser_assign: true,
                is_open: false,
                value: {
                  type_selection: [
                    { label: 'Android应用', value: 'ANDROID' },
                  ],
                  result_map: {},
                },
                backup_value: {
                  type_selection: [
                    { label: 'Android应用', value: 'ANDROID' },
                  ],
                  result_map: {},
                },
                isPreview: false,
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    if (Object.keys(value.result_map).length) {
                      callback();
                    } else {
                      callback(new Error('请选择投放内容'));
                    }
                  },
                },
              },
              marketing_goal: {
                label: '营销场景',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '短视频+图文', value: 'VIDEO_AND_IMAGE' },
                  { label: '直播', value: 'LIVE' },
                ],
                value: 'VIDEO_AND_IMAGE',
                formItemTips: {
                  props: {
                    class: 'color-red',
                  },
                  content: '直播营销场景仅支持已在头条平台内签署直播推广协议的广告主账户，使用前请检查是否符合要求',
                },
              },
              audience_type: {
                label: '定向类型',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '不限', value: 'UNLIMITED' },
                  { label: '自定义', value: 'CUSTOM' },
                ],
                value: 'CUSTOM',
                rules: { required: true, message: '请选择定向类型', trigger: ['change'] },
              },
              native_type: {
                label: '原生身份',
                type: 'radio-group',
                isButton: true,
                props: {
                  disabled: false,
                },
                options: [
                  { label: '账户信息', value: 'ACCOUNT' },
                  { label: '抖音号', value: 'AWEME' },
                ],
                value: 'AWEME',
                rules: { required: true, message: '请选择原生身份', trigger: ['change'] },
              },
              app_promotion_type: {
                label: '投放目的',
                type: 'radio-group',
                isButton: true,
                hidden: false,
                options: [
                  { label: '应用下载', value: 'DOWNLOAD' },
                  { label: '预约下载', value: 'RESERVE' },
                ],
                value: 'DOWNLOAD',
                backup_value: 'DOWNLOAD',
              },
              channel_package_id: {
                label: '应用渠道包',
                type: 'radio-group',
                isButton: true,
                options: [{ label: '自动配置', value: 'AUTO' }],
                value: 'AUTO',
                labelTips: generateHtmlContent({
                  content: [
                    '系统将自动根据已添加的渠道号关联媒体平台的渠道包(渠道标识)',
                    '若渠道包不存在，可前往媒体平台先行创建或预览时手动选择渠道包',
                  ],
                }),
              },
              download_type: {
                label: '下载方式',
                type: 'radio-group',
                isButton: true,
                hidden: false,
                props: {
                  disabled: false,
                },
                options: [
                  { label: '直接下载', value: 'DOWNLOAD_URL' },
                  { label: '落地页下载', value: 'EXTERNAL_URL' },
                  { label: '直接调起', value: 'QUICK_APP_URL' },
                ],
                value: 'DOWNLOAD_URL',
              },
              download_mode: {
                label: '优先应用商店下载',
                type: 'radio-group',
                isButton: true,
                hidden: false,
                options: [
                  { label: '启用', value: 'APP_STORE_DELIVERY' },
                  { label: '不启用', value: 'DEFAULT' },
                ],
                value: 'DEFAULT',
              },
              delivery_mode: {
                label: '投放方式',
                type: 'radio-group',
                isButton: true,
                props: {
                  disabled: true,
                },
                options: [
                  { label: '自动投放', value: 'PROCEDURAL' },
                ],
                value: 'PROCEDURAL',
                backup_value: 'PROCEDURAL',
              },
              // 关联星广联投选项
              star_task_switch: {
                label: '关联星广联投',
                type: 'radio-group',
                isButton: true,
                props: {
                  disabled: false,
                },
                options: [
                  { label: '启用', value: 'ON' },
                  { label: '不启用', value: 'OFF' },
                ],
                value: 'OFF',
              },
              // 关联星广联投
              star_tasks: {
                label: ' ',
                type: '',
                hidden: true,
                is_open: false,
                isPreview: false,
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    if (Object.keys(value).length && Object.values(value).every(item => item?.length)) {
                      callback();
                    } else {
                      callback(new Error('请选择关联星广联投'));
                    }
                  },
                },
                // 临时分配
                assignSelection: {},
                // 格式 - { [advertiser_id]: [{}, {}] }
                value: {},
              },
            },
          },
          site_form: {
            config: {
              site_set_type: {
                props: {
                  disabled: true,
                },
                value: 1,
              },
              site_sets: {
                hidden: true,
                value: ['INVENTORY_AWEME_FEED'],
                rules: { required: true, message: '请选择广告版位', trigger: ['change'] },
              },
            },
          },
          budget_and_bid_form: {
            config: {
              daily_budget_type: {
                props: {
                  disabled: true,
                },
                value: 'BUDGET_MODE_DAY',
              },
              daily_budget: {
                hidden: false,
              },
              price_type: {
                value: 'PRICING_OCPM',
              },
              price: {
                hidden: true,
              },
              price_strategy: {
                props: {
                  disabled: false,
                },
                hidden: false,
                value: 'CUSTOM',
              },
              convert_id: {
                hidden: true,
              },
              fusion__optimize_goal: {
                label: '优化目标',
                type: 'select',
                hidden: false,
                props: {
                  clearable: false,
                },
                options: [],
                value: '',
                backup_value: '',
                rules: { required: true, message: '请选择优化目标', trigger: ['change'] },
              },
              deep_convert_type: {
                label: '深度优化目标',
                type: 'select',
                hidden: true,
                options: [],
                value: '',
                backup_value: '',
              },
              deep_bid_type: {
                label: '深度优化方式',
                type: 'radio-group',
                isButton: true,
                hidden: true,
                options: [],
                value: '',
              },
              cpa_bid: {
                label: '优化目标出价',
                type: 'input-number',
                hidden: false,
                props: {
                  step: 1,
                  precision: 2,
                  min: 0.01,
                  max: 999999999.00,
                },
                formItemTips: {
                  props: {
                    class: 'inline-block',
                    style: {
                      marginLeft: '10px',
                    },
                  },
                  content: '元',
                },
                value: undefined,
                rules: { required: true, message: '请输入优化目标出价', trigger: ['change'] },
              },
              roi_goal: {
                label: '深度ROI系数出价',
                type: 'input-number',
                hidden: true,
                props: {
                  step: 1,
                  precision: 3,
                  min: 0.001,
                  max: 1000,
                },
                value: undefined,
                rules: { required: true, message: '请输入深度ROI系数出价', trigger: ['change'] },
              },
            },
          },
        },
      },
      creative_config: {
        config: {
          base_form: {
            config: {
              operation: {
                label: '创意状态',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '开启', value: 'ENABLE' },
                  { label: '关闭', value: 'DISABLE' },
                ],
                value: 'DISABLE',
              },
            },
          },
          content_form: {
            config: {
              delivery_mode: {
                label: '投放模式',
                type: 'radio-group',
                isButton: true,
                props: {
                  disabled: true,
                },
                options: [
                  { label: '组件化创意', value: 'DELIVERY_MODE_COMPONENT' },
                  { label: '自定义创意', value: 'DELIVERY_MODE_CUSTOMIZE' },
                ],
                value: 'DELIVERY_MODE_COMPONENT',
              },
              materials_type: {
                label: '素材类型',
                type: 'radio-group',
                isButton: true,
                hidden: false,
                options: [
                  { label: '广告素材', value: 'PROMOTION_MATERIALS' },
                  { label: '直播素材', value: 'LIVE_MATERIALS' },
                ],
                value: 'PROMOTION_MATERIALS',
              },
              videos_num_per_creative: {
                label: '每创意分配视频数',
                type: 'input-number',
                hidden: false,
                props: {
                  step: 1,
                  precision: 0,
                  min: 0,
                  max: 800,
                },
                value: 5,
              },
              images_num_per_creative: {
                label: '每创意分配图片数',
                type: 'select',
                hidden: false,
                options: getNumberOptions(0, 30),
                value: 0,
              },
              titles_num_per_creative: {
                label: '每创意分配标题数',
                type: 'select',
                hidden: false,
                options: getNumberOptions(1, 30),
                value: 5,
              },
              sites_num_per_creative: {
                label: '每创意分配落地页数',
                type: 'select',
                hidden: false,
                options: getNumberOptions(1, 10),
                value: 1,
              },
              // 抖音号ID - 非星广联投
              dou_yin_ids: {
                label: ' ',
                hidden: true,
                is_open: false,
                isPreview: false,
                // 临时分配
                assignSelection: {},
                // 格式 - { [advertiser_id]: [{}, {}] }
                value: {},
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    const is_valid = Object.keys(value).length && Object.entries(value).every(([, list]) => list.length);
                    if (is_valid) {
                      callback();
                    } else {
                      callback(new Error('请选择抖音号'));
                    }
                  },
                },
              },
              // 抖音号设置类型 - 星广联投
              aweme_setting_type: {
                label: ' ',
                type: 'radio-group',
                hidden: true,
                isButton: true,
                props: {
                  disabled: false,
                },
                options: [
                  { label: '全部抖音号', value: 'ALL' },
                  { label: '指定抖音号', value: 'MULTIPLE' },
                ],
                value: 'ALL',
                backup_value: 'ALL',
              },
              // 排除指定抖音号 - 星广联投
              exclude_aweme_ids: {
                label: '排除指定抖音号',
                hidden: true,
                is_open: false,
                isPreview: false,
                // 临时分配
                assignSelection: {},
                // 格式 - { [advertiser_id]: [{}, {}] }
                value: {},
                backup_value: {},
              },
              // 指定抖音号 - 星广联投
              aweme_ids: {
                label: '指定抖音号',
                hidden: true,
                is_open: false,
                isPreview: false,
                // 临时分配
                assignSelection: {},
                // 格式 - { [advertiser_id]: [{}, {}] }
                value: {},
                backup_value: {},
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    const is_valid = Object.keys(value).length && Object.entries(value).every(([, list]) => list.length);
                    if (is_valid) {
                      callback();
                    } else {
                      callback(new Error('请选择抖音号'));
                    }
                  },
                },
              },
              // 使用原视频标题
              original_video_title: {
                label: '使用原视频标题',
                type: 'radio-group',
                hidden: true,
                isButton: true,
                props: {
                  disabled: false,
                },
                options: [
                  { label: '启用', value: 'ON' },
                  { label: '不启用', value: 'OFF' },
                ],
                value: 'OFF',
              },
              byte_dance_games: {
                label: '小游戏链接',
                hidden: true,
                is_open: false,
                isPreview: false,
                value: {
                  result_value: [],
                },
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    if (value.result_value.length) {
                      callback();
                    } else {
                      callback(new Error('请选择小游戏链接'));
                    }
                  },
                },
              },
            },
          },
          creative_components: {
            config: {
              // 产品信息
              product_info: {
                value: {},
                children: {
                  titles: {
                    label: '产品名称',
                    type: 'input',
                    hidden: false,
                    props: {
                      showWordLimit: true,
                      maxlength: 20,
                    },
                    value: '',
                    rules: { required: true, message: '请输入产品名称', trigger: ['change'] },
                  },
                  image_ids: {
                    label: '产品主图',
                    hidden: false,
                    is_open: false,
                    isPreview: false,
                    limit: 10,
                    material_restriction: [{ width: 108, height: 108 }],
                    value: [],
                    rules: { required: true, message: '请选择产品主图', trigger: ['change'] },
                  },
                  selling_points: {
                    label: '产品卖点',
                    type: 'form-tags',
                    hidden: false,
                    props: {
                      direction: 'column',
                      multiple: false,
                      showWordLimit: true,
                      minlength: 1,
                      maxlength: 9,
                      tagsNum: 10,
                    },
                    value: [],
                    rules: { required: true, message: '请添加产品卖点', trigger: ['change'] },
                  },
                },
              },
              // 行动号召
              call_to_action_buttons: {
                label: '行动号召',
                type: 'form-tags',
                hidden: false,
                props: {
                  direction: 'column',
                  multiple: false,
                  showWordLimit: true,
                  minlength: 1,
                  maxlength: 6,
                  tagsNum: 10,
                },
                value: [],
                rules: { required: true, message: '请添加行动号召', trigger: ['change'] },
              },
              // 广告来源
              source: {
                label: '广告来源',
                type: 'input',
                hidden: false,
                props: {
                  showWordLimit: true,
                  maxlength: 10,
                },
                value: '',
                rules: { required: true, message: '请输入广告来源', trigger: ['change'] },
              },
              star_task_author_videos: {
                label: '达人投稿视频',
                hidden: true,
                is_open: false,
                isPreview: false,
                // 最小选择数
                min_selection_count: 10,
                /**
                 * 过滤配置 - 用于窗口二次拉起时指定过滤条件
                 * 格式：
                 * {
                 *   '过滤项字段': {
                 *     '广告账户ID': '过滤项原始值'
                 *   }
                 * }
                 */
                filter_config: {},
                filter_config_backup: {},
                // 用于弹窗中表格接口传参判断
                api_params: {
                  aweme_setting_type: 'ALL',
                  exclude_aweme_ids: {},
                  aweme_ids: {},
                },
                // 临时分配
                assignSelection: {},
                // 格式 - { [advertiser_id]: [{}, {}] }
                value: {},
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    const is_valid = Object.keys(value).length && Object.entries(value).every(([, list]) => list.length);
                    if (is_valid) {
                      callback();
                    } else {
                      callback(new Error('请选择达人投稿视频'));
                    }
                  },
                },
              },
              // 每创意分配投稿视频数
              star_task_author_videos_num_per_creative: {
                label: '每创意分配投稿视频数',
                type: 'select',
                hidden: true,
                options: getNumberOptions(1, 10),
                value: 10,
                rules: { required: true, message: '请选择每创意分配投稿视频数', trigger: ['change'] },
              },
              // 原生锚点
              anchor_related_type: {
                label: '原生锚点',
                type: 'radio-group',
                hidden: false,
                isButton: true,
                props: {
                  disabled: false,
                },
                options: [
                  { label: '不启用', value: 'OFF' },
                  { label: '自动生成', value: 'AUTO' },
                  { label: '手动选择', value: 'SELECT' },
                ],
                value: 'OFF',
              },
              anchor_id: {
                label: ' ',
                hidden: true,
                is_open: false,
                isPreview: false,
                value: {
                  type_selection: [
                    { label: '应用下载-游戏', value: 'APP_GAME' },
                  ],
                  result_value: [],
                },
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    if (value.result_value.length) {
                      callback();
                    } else {
                      callback(new Error('请选择原生锚点'));
                    }
                  },
                },
              },
              // 品牌名称
              cdp_brand_id: {
                label: '品牌名称',
                type: 'select',
                options: [],
                value: '',
                rules: { required: true, message: '请选择品牌名称', trigger: ['change'] },
              },
              // 所属类别
              yuntu_category_id: {
                label: '所属类别',
                type: 'cascader',
                props: {
                  props: {
                    emitPath: false,
                  },
                },
                options: [],
                value: '',
                rules: { required: true, message: '请选择所属类别', trigger: ['change'] },
              },
            },
          },
          creative_price: {
            title: '创意出价',
            config: {
              cpa_bid: {
                label: '优化目标出价',
                type: 'input-number',
                hidden: true,
                props: {
                  step: 1,
                  precision: 2,
                  min: 0.01,
                  max: 999999999.00,
                },
                formItemTips: {
                  props: {
                    class: 'inline-block',
                    style: {
                      marginLeft: '10px',
                    },
                  },
                  content: '元',
                },
                value: undefined,
                rules: { required: true, message: '请输入优化目标出价', trigger: ['change'] },
              },
              roi_goal: {
                label: '深度ROI系数出价',
                type: 'input-number',
                hidden: true,
                props: {
                  step: 1,
                  precision: 3,
                  min: 0.001,
                  max: 1000,
                },
                value: undefined,
                rules: { required: true, message: '请输入深度ROI系数出价', trigger: ['change'] },
              },
            },
          },
        },
      },
      targeting_config: {
        config: {
          targeting_form: {
            config: {
              // 地域
              geo_location: {
                children: {
                  location_type: {
                    labelTips: generateHtmlContent({
                      content: [
                        '常住地代表居住在该地区的用户',
                        '近期到访代表正在该地区的用户',
                      ],
                      is_index: false,
                    }),
                  },
                },
              },
              // 自定义人群
              audience: {
                children: {
                  audience_types: {
                    labelTips: '注意：投放方式为自动投放时不支持使用定向人群配置',
                  },
                },
              },
              gender: {
                value: 'NONE',
              },
              // 年龄
              ages: {
                value: {},
                children: {
                  ages_selection: {
                    label: '年龄',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  ages: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    isBox: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择年龄', trigger: ['change'] },
                  },
                },
              },
              // 行为兴趣
              interest_action: {
                value: {},
                children: {
                  interest_action_mode: {
                    label: '行为兴趣',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: 'UNLIMITED' },
                      { label: '系统推荐', value: 'RECOMMEND' },
                      { label: '自定义', value: 'CUSTOM' },
                    ],
                    value: 'UNLIMITED',
                    labelTips: '注意：投放方式为自动投放时不支持使用行为兴趣定向配置',
                  },
                  action_days: {
                    label: '用户行为天数',
                    type: 'select',
                    hidden: true,
                    options: [],
                    value: 30,
                    rules: { required: true, message: '请选择用户行为天数', trigger: ['change'] },
                  },
                  action_categories: {
                    label: '行为类目',
                    type: 'tree-shuttle',
                    name: '行为类目',
                    props: {
                      nodeKey: 'value',
                      maxCount: 300,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择行为类目'));
                        }
                      },
                    },
                  },
                  interest_categories: {
                    label: '兴趣类目',
                    type: 'tree-shuttle',
                    name: '兴趣类目',
                    props: {
                      nodeKey: 'value',
                      maxCount: 300,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择兴趣类目'));
                        }
                      },
                    },
                  },
                },
              },
              // 抖音达人
              aweme_fan: {
                value: {},
                children: {
                  aweme_fan_selection: {
                    label: '抖音达人',
                    type: 'checkbox-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '抖音达人分类', value: 'aweme_fan_categories' },
                      { label: '抖音达人', value: 'aweme_fan_accounts' },
                    ],
                    value: [OPTIONS_TYPE.UNLIMITED],
                    labelTips: '注意：投放方式为自动投放时不支持使用抖音达人定向配置',
                  },
                  aweme_fan_behaviors: {
                    label: '互动行为类型',
                    type: 'select',
                    props: {
                      multiple: true,
                    },
                    hidden: true,
                    options: [],
                    value: ['FOLLOWED_USER'],
                    rules: { required: true, message: '请选择互动行为类型', trigger: ['change'] },
                  },
                  aweme_fan_time_scope: {
                    label: '互动行为天数',
                    type: 'select',
                    hidden: true,
                    options: [],
                    value: 'FIFTEEN_DAYS',
                    rules: { required: true, message: '请选择互动行为天数', trigger: ['change'] },
                  },
                  aweme_fan_categories: {
                    label: '抖音达人分类',
                    type: 'tree-shuttle',
                    name: '抖音达人分类',
                    props: {
                      nodeKey: 'value',
                      maxCount: 30,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择抖音达人分类'));
                        }
                      },
                    },
                  },
                  aweme_fan_accounts: {
                    label: '抖音达人',
                    type: 'tree-shuttle',
                    name: '抖音达人',
                    props: {
                      nodeKey: 'value',
                      maxCount: 30,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择抖音达人'));
                        }
                      },
                    },
                  },
                },
              },
              // 穿山甲媒体定向
              superior_popularity_type: {
                value: {},
                children: {
                  superior_popularity_type: {
                    label: '行为兴趣',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '定向媒体包', value: 'flow_package' },
                      { label: '排除媒体包', value: 'exclude_flow_package' },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: '注意：投放方式为自动投放时不支持使用穿山甲媒体定向配置',
                  },
                  flow_packages: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '定向媒体包',
                    props: {
                      nodeKey: 'value',
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择定向媒体包'));
                        }
                      },
                    },
                  },
                  exclude_flow_packages: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '排除媒体包',
                    props: {
                      nodeKey: 'value',
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择排除媒体包'));
                        }
                      },
                    },
                  },
                },
              },
              // 系统平台
              platforms: {
                label: '系统平台',
                type: 'checkbox-group',
                isButton: true,
                options: [
                  { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                  { label: '安卓', value: 'ANDROID' },
                  { label: 'IOS', value: 'IOS' },
                ],
                value: [OPTIONS_TYPE.UNLIMITED],
                labelTips: generateHtmlContent({
                  content: [
                    '投放内容为Android应用时，系统平台定向仅支持安卓',
                    '投放内容为iOS应用时，系统平台定向仅支持iOS',
                    '投放方式为自动投放且投放内容为Android应用或iOS应用时，不支持使用系统平台定向配置',
                  ],
                }),
              },
              // 安卓版本
              android_osv: {
                value: {},
                children: {
                  android_osv_selection: {
                    label: '安卓版本',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: generateHtmlContent({
                      content: [
                        '最低安卓版本要求',
                        '例：选择Android 2.0即代表定向Android 2.0版本及以上的用户',
                        '注意：投放内容为iOS应用或微信小游戏或字节小游戏时，不支持使用安卓版本定向配置',
                      ],
                      is_index: false,
                    }),
                  },
                  android_osv: {
                    label: ' ',
                    type: 'select',
                    hidden: true,
                    options: [],
                    value: '2.0',
                    rules: { required: true, message: '请选择安卓版本', trigger: ['change'] },
                  },
                },
              },
              // IOS版本
              ios_osv: {
                value: {},
                children: {
                  ios_osv_selection: {
                    label: 'IOS版本',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: generateHtmlContent({
                      content: [
                        '最低iOS版本要求',
                        '例：选择iOS 4.0即代表定向iOS 4.0版本及以上的用户',
                        '注意：投放内容为Android应用或微信小游戏或字节小游戏时，不支持使用iOS版本定向配置',
                      ],
                      is_index: false,
                    }),
                  },
                  ios_osv: {
                    label: ' ',
                    type: 'select',
                    hidden: true,
                    options: [],
                    value: '4.0',
                    rules: { required: true, message: '请选择IOS版本', trigger: ['change'] },
                  },
                },
              },
              // 设备类型
              device_types: {
                label: '设备类型',
                type: 'checkbox-group',
                isButton: true,
                options: [
                  { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                  { label: '智能手机', value: 'MOBILE' },
                  { label: '平板', value: 'PAD' },
                ],
                value: [OPTIONS_TYPE.UNLIMITED],
                labelTips: '注意：投放方式为自动投放时不支持使用设备类型定向配置',
              },
              // 设备品牌
              device_brands: {
                value: {},
                children: {
                  device_brands_selection: {
                    label: '设备品牌',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: generateHtmlContent({
                      content: [
                        '投放方式为自动投放时不支持使用设备品牌定向配置',
                        '投放内容为Android应用时，不支持选择设备品牌定向-苹果配置选项',
                        '投放内容为iOS应用时，不支持选择设备品牌定向-非苹果配置选项',
                      ],
                    }),
                  },
                  device_brands: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '设备品牌',
                    props: {
                      nodeKey: 'value',
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择设备品牌'));
                        }
                      },
                    },
                  },
                },
              },
              // 设备价格
              launch_price: {
                value: {},
                children: {
                  launch_price_selection: {
                    label: '设备价格',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: '注意：投放方式为自动投放时不支持使用设备价格定向配置',
                  },
                  launch_price: {
                    label: ' ',
                    type: 'form-range',
                    hidden: true,
                    value: { start: 0, end: 11000 },
                    props: {
                      startOptions: [{ label: '0元', value: 0 }, ...getLaunchPriceOptions()],
                      endOptions: [{ label: '不限', value: 11000 }, ...getLaunchPriceOptions()],
                    },
                    rules: {
                      required: true,
                      trigger: ['change'],
                      validator: (_, value, callback) => {
                        if (!(value.start === 0 || value.start) || !value.end) {
                          callback(new Error('请选择设备价格'));
                        } else if (value.start > value.end || value.start === value.end) {
                          callback(new Error('起始值不能大于等于结束值'));
                        } else {
                          callback();
                        }
                      },
                    },
                  },
                },
              },
              // 网络环境
              network_types: {
                label: '网络环境',
                type: 'checkbox-group',
                isButton: true,
                options: [],
                value: [OPTIONS_TYPE.UNLIMITED],
                labelTips: '注意：投放方式为自动投放时不支持使用网络环境定向配置',
              },
              // 已安装用户
              hide_if_exists: {
                label: '已安装用户',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '不限', value: 'UNLIMITED' },
                  { label: '过滤', value: 'FILTER' },
                  { label: '定向', value: 'TARGETING' },
                ],
                value: 'UNLIMITED',
                labelTips: generateHtmlContent({
                  content: [
                    '投放内容为iOS应用或微信小游戏或字节小游戏时，不支持使用已安装用户定向配置',
                    '投放内容为Android应用且投放方式为自动投放时，不支持使用已安装用户定向-定向配置选项',
                  ],
                }),
              },
              // 过滤已转化用户
              hide_if_converted: {
                value: {},
                children: {
                  hide_if_converted: {
                    label: '过滤已转化用户',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: 'NO_EXCLUDE' },
                      { label: '广告', value: 'PROMOTION' },
                      { label: '项目', value: 'PROJECT' },
                      { label: '广告账户', value: 'ADVERTISER' },
                      { label: '公司账户', value: 'CUSTOMER' },
                      { label: '组织账户', value: 'ORGANIZATION' },
                      { label: '应用', value: 'APP' },
                    ],
                    value: 'NO_EXCLUDE',
                    labelTips: '注意：投放内容为微信小游戏或字节小游戏时，不支持使用过滤已转化用户定向-应用配置选项',
                  },
                  converted_time_duration: {
                    label: '过滤时间范围',
                    type: 'select',
                    hidden: true,
                    options: [
                      { label: '当天', value: 'TODAY' },
                      { label: '7天', value: 'SEVEN_DAY' },
                      { label: '1个月', value: 'ONE_MONTH' },
                      { label: '3个月', value: 'THREE_MONTH' },
                      { label: '6个月', value: 'SIX_MONTH' },
                      { label: '12个月', value: 'TWELVE_MONTH' },
                    ],
                    value: 'THREE_MONTH',
                    rules: { required: true, message: '请选择过滤时间范围', trigger: ['change'] },
                  },
                },
              },
              // 智能扩量
              auto_extend_targets: {
                value: {},
                children: {
                  auto_extend_targets_selection: {
                    label: '智能扩量',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不开启', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '开启', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                    labelTips: '注意：投放方式为自动投放时不支持使用智能扩量定向配置',
                  },
                  auto_extend_targets: {
                    label: ' ',
                    type: 'checkbox-group',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '地域', value: 'REGION', disabled: true },
                      { label: '性别', value: 'GENDER', disabled: true },
                      { label: '年龄', value: 'AGE', disabled: true },
                      { label: '自定义人群', value: 'CUSTOM_AUDIENCE', disabled: true },
                    ],
                    value: [],
                    rules: { required: true, message: '请选择智能扩量', trigger: ['change'] },
                  },
                },
              },
            },
          },
        },
      },
    };
  }

  /**
   * 添加事件
   * @param {Object} config 模块配置
   * @param {Object} controller 控制器
   */
  addEvent(config, controller) {
    this.config = config;
    super.addEvent(config);
    const {
      campaign_config: {
        config: {
          delivery_form,
          site_form,
          budget_and_bid_form,
        },
      },
    } = config;
    const {
      delivery_platform_type,
      delivery_content_id,
      marketing_goal,
      native_type,
      app_promotion_type,
      download_type,
      download_mode,
      delivery_mode,
      star_task_switch,
      star_tasks,
    } = delivery_form.config;
    const {
      site_set_type,
      site_sets,
    } = site_form.config;
    const {
      daily_budget_type,
      daily_budget,
      price_type,
      price,
      price_strategy,
      fusion__optimize_goal,
      deep_convert_type,
      deep_bid_type,
      cpa_bid,
      roi_goal,
    } = budget_and_bid_form.config;
    let syncStarTaskCreativeFields = () => {};

    const setPriceState = () => {
      const is_ocpx = price_type.value === 'PRICING_OCPM';
      const is_harmony_cpc_or_cpm = isHarmonyCpcOrCpm(delivery_platform_type.value, price_type.value);
      price.hidden = is_ocpx || is_harmony_cpc_or_cpm;
      price.rules.required = !is_harmony_cpc_or_cpm;
      is_harmony_cpc_or_cpm && (price.value = undefined);
    };

    const syncNativeTypeField = () => {
      const is_live = marketing_goal.value === 'LIVE';
      const is_star_tasks_on = star_task_switch.value === 'ON';
      /**
       * 原生身份对齐旧版推广身份：
       * - 关联星广联投为【启用(ON)】或营销场景为【直播['LIVE']】时禁选
       * - 关联星广联投为【启用(ON)】或营销场景为【直播['LIVE']】时默认选中【抖音号(AWEME)】
       */
      (is_star_tasks_on || is_live) && (native_type.value = 'AWEME');
      native_type.props.disabled = is_star_tasks_on || is_live;
    };

    /**
     * 设置优化目标和深度优化目标
     * @returns {Promise<void>}
     */
    const setConvertData = async() => {
      const delivery_content_list = Object.entries(delivery_content_id.value.result_map);
      if (!delivery_content_list.length) {
        return;
      }
      const first_delivery_content = delivery_content_list[0][1];
      // 获取优化目标
      fusion__optimize_goal.options = await controller.getConvertGoalList({
        jrtt_v3_params: {
          filtering: {
            delivery_platform_type: delivery_platform_type.value,
            delivery_mode: delivery_mode.value,
            marketing_goal: marketing_goal.value,
            delivery_content_id: first_delivery_content.value.split('#')[0],
            ...(!app_promotion_type.hidden && { app_promotion_type: app_promotion_type.value }),
          },
        },
      });
      const is_match_optimize_goal = fusion__optimize_goal.options.some(({ value }) => fusion__optimize_goal.value === value);
      if (!is_match_optimize_goal) {
        fusion__optimize_goal.value = '';
        fusion__optimize_goal.backup_value = '';
        deep_convert_type.value = '';
        deep_convert_type.options = [];
      }

      // 获取深度优化目标
      if (fusion__optimize_goal.value) {
        deep_convert_type.options = await controller.getDeepConvertGoalList({
          jrtt_v3_params: {
            filtering: {
              delivery_platform_type: delivery_platform_type.value,
              delivery_mode: delivery_mode.value,
              convert_goal: fusion__optimize_goal.value,
              ...(!app_promotion_type.hidden && { app_promotion_type: app_promotion_type.value }),
              delivery_content_id: first_delivery_content.value.split('#')[0],
              marketing_goal: marketing_goal.value,
            },
          },
        });
        const is_match_deep_convert_type = deep_convert_type.options.some(({ value }) => deep_convert_type.value === value);
        if (!is_match_deep_convert_type) {
          deep_convert_type.value = '';
          deep_convert_type.backup_value = deep_convert_type.value;
        }

        await setDeepBidTypeData();
      }
    };

    /**
     * 设置深度优化方式
     * @returns {Promise<void>}
     */
    const setDeepBidTypeData = async() => {
      const is_pay = fusion__optimize_goal.value === 'AD_CONVERT_TYPE_PAY';
      if (is_pay || (fusion__optimize_goal.value && deep_convert_type.value)) {
        deep_bid_type.options = await controller.getDeepBidTypeList({
          jrtt_v3_params: {
            filtering: {
              delivery_platform_type: delivery_platform_type.value,
              delivery_mode: delivery_mode.value,
              convert_goal: fusion__optimize_goal.value,
              ...(deep_convert_type.value && { deep_convert_goal: deep_convert_type.value }),
              marketing_goal: marketing_goal.value,
            },
          },
        });
        const is_match_deep_bid_type = deep_bid_type.options.some(({ value }) => deep_bid_type.value === value);
        !is_match_deep_bid_type && (deep_bid_type.value = deep_bid_type.options.length ? deep_bid_type.options[0].value : '');
      } else {
        deep_bid_type.options = [];
        deep_bid_type.value = '';
      }
    };

    // 投放平台
    delivery_platform_type.on = {
      change: async() => {
        const is_android = delivery_platform_type.value === 'ANDROID';
        const is_harmony = delivery_platform_type.value === 'HARMONY';
        const is_ios = delivery_platform_type.value === 'IOS';

        app_promotion_type.hidden = !(is_android || is_harmony || is_ios);
        download_type.hidden = !(is_android || is_harmony || is_ios);
        download_mode.hidden = !is_android;
        app_promotion_type.on.change();

        if (delivery_platform_type.value !== delivery_platform_type.backup_value) {
          delivery_platform_type.backup_value = delivery_platform_type.value;

          delivery_content_id.value.result_map = {};
        }
        delivery_content_id.value?.type_selection?.splice?.(0);
        delivery_content_id.value?.type_selection?.push?.(...[
          ...(delivery_platform_type.value === 'ANDROID' ? [{ label: 'Android应用', value: 'ANDROID' }] : []),
          ...(delivery_platform_type.value === 'IOS' ? [{ label: 'IOS应用', value: 'IOS' }] : []),
          ...(delivery_platform_type.value === 'WECHAT_MINI_GAME' ? [{ label: '微信小游戏', value: 'WECHAT_MINI_GAME' }] : []),
          ...(delivery_platform_type.value === 'HARMONY' ? [{ label: '鸿蒙应用', value: 'HARMONY' }] : []),
          ...(delivery_platform_type.value === 'MICRO_GAME' ? [{ label: '字节小游戏', value: 'MICRO_GAME' }] : []),
        ]);

        site_set_type.on.change();
        setPriceState();
      },
    };

    // 投放内容
    delivery_content_id.on = {
      change: async() => {
        const is_change = JSON.stringify(delivery_content_id.value) !== JSON.stringify(delivery_content_id.backup_value);
        if (is_change) {
          delivery_content_id.backup_value = JSON.parse(JSON.stringify(delivery_content_id.value));
          await setConvertData();
        }
        price_type.on.change();
      },
    };

    // 营销场景
    marketing_goal.on = {
      change: async() => {
        const is_live = marketing_goal.value === 'LIVE';

        /**
         * 营销场景为【直播['LIVE']】时，
         * - 关联星广联投选项禁选
         * - 关联星广联投值为【不启用(OFF)】
         */
        star_task_switch.props.disabled = is_live;
        is_live && (star_task_switch.value = 'OFF');
        star_task_switch.on.change();

        site_set_type.on.change();
        await setConvertData();
      },
    };

    // 投放目的
    app_promotion_type.on = {
      change: async() => {
        const is_android = delivery_platform_type.value === 'ANDROID';
        const is_visible = !app_promotion_type.hidden;
        const is_reserve = app_promotion_type.value === 'RESERVE';

        download_mode.hidden = !(is_android && !is_reserve);
        download_type.props.disabled = is_visible && is_reserve;
        (is_visible && is_reserve) && (download_type.value = 'EXTERNAL_URL');

        const is_change = JSON.stringify(app_promotion_type.value) !== JSON.stringify(app_promotion_type.backup_value);
        if (is_change) {
          app_promotion_type.backup_value = app_promotion_type.value;
          await setConvertData();
        }
      },
    };

    // 投放方式
    delivery_mode.on = {
      change: async() => {
        if (delivery_mode.value !== delivery_mode.backup_value) {
          delivery_mode.backup_value = delivery_mode.value;
          await setConvertData();
        }
        star_task_switch.on.change();
        const is_procedural = delivery_mode.value === 'PROCEDURAL';

        site_set_type.props.disabled = is_procedural;
        is_procedural && (site_set_type.value = 1);
        site_set_type.on.change();

        daily_budget_type.props.disabled = is_procedural;
        is_procedural && (daily_budget_type.value = 'BUDGET_MODE_DAY');
        daily_budget_type.on.change();

        price_type.on.change();
      },
    };

    // 关联星广联投选项
    star_task_switch.on = {
      change: () => {
        const is_custom = star_task_switch.value === 'ON';
        star_tasks.hidden = !is_custom;
        syncNativeTypeField();
        syncStarTaskCreativeFields();
      },
    };

    // 版位类型
    site_set_type.on = {
      change: () => {
        const is_auto = site_set_type.value === 1;
        site_sets.hidden = is_auto;
        site_sets.on?.change?.();
      },
    };

    // 日预算类型
    daily_budget_type.on = {
      change: () => {
        const is_day = daily_budget_type.value === 'BUDGET_MODE_DAY';
        daily_budget.hidden = !is_day;
        price_strategy.on.change();
      },
    };

    // 出价方式
    price_type.on = {
      change: () => {
        const is_ocpx = price_type.value === 'PRICING_OCPM';
        setPriceState();
        price_strategy.hidden = !is_ocpx;
        fusion__optimize_goal.hidden = !is_ocpx;
        price_strategy.on.change();
      },
    };

    // 出价策略
    price_strategy.on = {
      change: () => {
        const is_procedural = delivery_mode.value === 'PROCEDURAL';
        const is_ocpx = price_type.value === 'PRICING_OCPM';
        const is_max = price_strategy.value === 'NO_BID';
        cpa_bid.hidden = !(is_procedural && is_ocpx && !is_max);
        fusion__optimize_goal.on.change();
      },
    };

    // 优化目标
    fusion__optimize_goal.on = {
      change: async() => {
        if (fusion__optimize_goal.value !== fusion__optimize_goal.backup_value) {
          fusion__optimize_goal.backup_value = fusion__optimize_goal.value;

          // 获取深度优化目标
          const delivery_content_list = Object.entries(delivery_content_id.value.result_map);
          if (delivery_content_list.length && fusion__optimize_goal.value) {
            const first_delivery_content = delivery_content_list[0][1];
            deep_convert_type.options = await controller.getDeepConvertGoalList({
              jrtt_v3_params: {
                filtering: {
                  delivery_platform_type: delivery_platform_type.value,
                  delivery_mode: delivery_mode.value,
                  convert_goal: fusion__optimize_goal.value,
                  ...(!app_promotion_type.hidden && { app_promotion_type: app_promotion_type.value }),
                  delivery_content_id: first_delivery_content.value.split('#')[0],
                  marketing_goal: marketing_goal.value,
                },
              },
            });
            const is_match_deep_convert_type = deep_convert_type.options.some(({ value }) => deep_convert_type.value === value);
            if (!is_match_deep_convert_type) {
              deep_convert_type.value = '';
              deep_convert_type.backup_value = deep_convert_type.value;
            }

            await setDeepBidTypeData();
          }
        }
        const is_ocpx = price_type.value === 'PRICING_OCPM';
        const is_has_optimize_goal = !!fusion__optimize_goal.value;

        deep_convert_type.hidden = !(is_ocpx && is_has_optimize_goal && deep_convert_type.options.length);
        deep_convert_type.on.change();
      },
    };

    // 深度优化目标
    deep_convert_type.on = {
      change: async() => {
        if (deep_convert_type.value !== deep_convert_type.backup_value) {
          deep_convert_type.backup_value = deep_convert_type.value;
          await setDeepBidTypeData();
        }
        const is_ocpx = price_type.value === 'PRICING_OCPM';
        const is_pay = fusion__optimize_goal.value === 'AD_CONVERT_TYPE_PAY';
        const is_has_optimize_goal = !!fusion__optimize_goal.value;
        const is_has_deep_convert_type = !!deep_convert_type.value;
        deep_bid_type.hidden = !(is_ocpx && (is_pay || (is_has_optimize_goal && is_has_deep_convert_type)) && deep_bid_type.options.length);
        deep_bid_type.on.change();
      },
    };

    // 深度优化方式
    deep_bid_type.on = {
      change: () => {
        const is_procedural = delivery_mode.value === 'PROCEDURAL';
        const is_ocpx = price_type.value === 'PRICING_OCPM';
        const is_max = price_strategy.value === 'NO_BID';
        const is_has_optimize_goal = !!fusion__optimize_goal.value;
        const is_has_deep_convert_type = !!deep_convert_type.value;
        const is_deep_bid_min = deep_bid_type.value === 'DEEP_BID_MIN';
        const is_roi = ['ROI_COEFFICIENT', 'FIRST_AND_SEVEN_PAY_ROI', 'PER_AND_SEVEN_PAY_ROI'].includes(deep_bid_type.value);
        roi_goal.hidden = !(is_procedural && is_ocpx && !is_max && is_has_optimize_goal && is_has_deep_convert_type && is_roi);

        if (is_deep_bid_min && is_max) {
          price_strategy.value = 'CUSTOM';
          price_strategy.on.change();
        }
        price_strategy.props.disabled = is_deep_bid_min;
      },
    };

    const {
      creative_config: {
        config: {
          content_form,
          creative_components,
        },
      },
    } = config;
    const {
      materials_type,
      videos_num_per_creative,
      images_num_per_creative,
      titles_num_per_creative,
      sites_num_per_creative,
      dou_yin_ids,
      aweme_setting_type,
      exclude_aweme_ids,
      aweme_ids,
    } = content_form.config;
    const {
      product_info: {
        children: {
          titles,
          image_ids,
          selling_points,
        },
      },
      call_to_action_buttons,
      source,
      star_task_author_videos,
      star_task_author_videos_num_per_creative,
      anchor_related_type,
      anchor_id,
      yuntu_category_id,
      cdp_brand_id,
    } = creative_components.config;

    const getSelectedBrandOption = () => {
      return cdp_brand_id.options.find(({ value, jrtt_v3_params = {}}) => {
        const [cdp_brand_id_value, cdp_brand_name_value] = String(cdp_brand_id.value || '').split('#');
        return value === cdp_brand_id.value ||
          (String(jrtt_v3_params.cdp_brand_id) === cdp_brand_id_value && jrtt_v3_params.cdp_brand_name === cdp_brand_name_value);
      });
    };

    const setBrandCategoryData = async() => {
      yuntu_category_id.options = [];
      const selected_brand = getSelectedBrandOption();
      const outer_brand_id = selected_brand?.jrtt_v3_params?.brand_name_id;
      if (!outer_brand_id) {
        return;
      }
      yuntu_category_id.options = await controller.getBrandCategoryList({ outer_brand_id });
    };

    // 品牌形象
    cdp_brand_id.on = {
      change: async() => {
        const is_change = JSON.stringify(cdp_brand_id.value) !== JSON.stringify(cdp_brand_id.backup_value);
        is_change && (yuntu_category_id.value = '');
        cdp_brand_id.backup_value = cdp_brand_id.value;
        await setBrandCategoryData();
      },
    };

    // 素材类型
    materials_type.on = {
      change: () => {
        const is_app = ['ANDROID', 'IOS', 'HARMONY'].includes(delivery_platform_type.value);
        const is_live = marketing_goal.value === 'LIVE';
        const is_visible = !materials_type.hidden;
        const is_promotion_materials = materials_type.value === 'PROMOTION_MATERIALS';
        const is_live_materials = materials_type.value === 'LIVE_MATERIALS';
        const is_aweme = native_type.value === 'AWEME';

        /**
         * 以下条件之一时，下列表单项 可见
         * - 素材类型不可见
         * - 素材类型可见 且 素材类型为【广告素材['PROMOTION_MATERIALS']】
         */
        [
          videos_num_per_creative,
          images_num_per_creative,
          titles_num_per_creative,
          titles,
          image_ids,
          selling_points,
          call_to_action_buttons,
          source,
          anchor_related_type,
        ].forEach(item => {
          item.hidden = !(!is_visible || (is_visible && is_promotion_materials));
        });
        anchor_related_type.hidden = !(is_aweme && (!is_visible || (is_visible && is_promotion_materials)));
        anchor_related_type.on.change();

        /**
         * 以下条件之一时，每创意分配落地页数 不可见
         * - 素材类型可见 且 投放平台为【Android应用['ANDROID']】、【IOS应用['IOS']】或【鸿蒙应用['HARMONY']】
         *   - 素材类型为【直播素材['LIVE_MATERIALS']】
         *   - 素材类型为【广告素材['PROMOTION_MATERIALS']】 且 营销场景为【直播['LIVE']】
         */
        sites_num_per_creative.hidden = is_visible && is_app && (is_live_materials || (is_promotion_materials && is_live));
      },
    };

    // 抖音号设置类型
    aweme_setting_type.on = {
      change: () => {
        const is_change = aweme_setting_type.value !== aweme_setting_type.backup_value;
        if (is_change) {
          aweme_setting_type.backup_value = aweme_setting_type.value;

          // 清空达人投稿视频的值
          star_task_author_videos.value = {};
        }
        star_task_author_videos.api_params.aweme_setting_type = aweme_setting_type.value;
        const is_all = aweme_setting_type.value === 'ALL';
        const is_multiple = aweme_setting_type.value === 'MULTIPLE';
        const is_star_tasks_on = star_task_switch.value === 'ON';
        const is_aweme = native_type.value === 'AWEME';

        /**
         * 排除指定抖音号，满足以下条件时显示
         * - 关联星广联投为【启用(ON)】
         * - 原生身份为【抖音号】
         * - 抖音号设置类型为【全部抖音号(ALL)】
         */
        exclude_aweme_ids.hidden = !(is_star_tasks_on && is_aweme && is_all);

        /**
         * 指定抖音号，满足以下条件时显示
         * - 关联星广联投为【启用(ON)】
         * - 原生身份为【抖音号】
         * - 抖音号设置类型为【指定抖音号(MULTIPLE)】
         */
        aweme_ids.hidden = !(is_star_tasks_on && is_aweme && is_multiple);
      },
    };
    syncStarTaskCreativeFields = () => {
      const is_star_tasks_on = star_task_switch.value === 'ON';
      const is_aweme = native_type.value === 'AWEME';
      dou_yin_ids.hidden = !(!is_star_tasks_on && is_aweme);
      aweme_setting_type.hidden = !(is_star_tasks_on && is_aweme);
      aweme_setting_type.on.change();
    };

    // 排除指定抖音号
    exclude_aweme_ids.on = {
      change: () => {
        const is_change = JSON.stringify(exclude_aweme_ids.value) !== JSON.stringify(exclude_aweme_ids.backup_value);
        if (is_change) {
          exclude_aweme_ids.backup_value = JSON.parse(JSON.stringify(exclude_aweme_ids.value));

          // 清空达人投稿视频的值
          star_task_author_videos.value = {};
        }
        star_task_author_videos.api_params.exclude_aweme_ids = JSON.parse(JSON.stringify(exclude_aweme_ids.value));
      },
    };

    // 排除指定抖音号
    aweme_ids.on = {
      change: () => {
        const is_change = JSON.stringify(aweme_ids.value) !== JSON.stringify(aweme_ids.backup_value);
        if (is_change) {
          aweme_ids.backup_value = JSON.parse(JSON.stringify(aweme_ids.value));

          // 清空达人投稿视频的值
          star_task_author_videos.value = {};
        }
        star_task_author_videos.api_params.aweme_ids = JSON.parse(JSON.stringify(aweme_ids.value));
      },
    };

    // 每创意分配投稿视频数
    star_task_author_videos_num_per_creative.on = {
      change: () => {
        // 同步达人投稿视频最小选择数
        star_task_author_videos.min_selection_count = star_task_author_videos_num_per_creative.value;
      },
    };

    // 原生锚点
    anchor_related_type.on = {
      change: () => {
        const is_visible = !anchor_related_type.hidden;
        const is_select = anchor_related_type.value === 'SELECT';
        /**
         * 原生锚点，满足以下条件时显示
         * - 原生锚点选项可见
         * - 原生锚点选项为【手动选择(SELECT)】
         */
        anchor_id.hidden = !(is_visible && is_select);
      },
    };

    const { targeting_config: { config: { targeting_form }}} = config;
    const {
      geo_location: {
        children: {
          geo_type,
          location_type,
          residence_geo_locations,
          current_geo_locations,
        },
      },
      gender,
      audience: {
        children: {
          audience_types,
          include_audiences,
          exclude_audiences,
        },
      },
      ages: {
        children: {
          ages_selection,
          ages,
        },
      },
      interest_action: {
        children: {
          interest_action_mode,
          action_days,
          action_categories,
          interest_categories,
        },
      },
      aweme_fan: {
        children: {
          aweme_fan_selection,
          aweme_fan_behaviors,
          aweme_fan_time_scope,
          aweme_fan_categories,
          aweme_fan_accounts,
        },
      },
      superior_popularity_type: {
        children: {
          superior_popularity_type,
          flow_packages,
          exclude_flow_packages,
        },
      },
      platforms,
      android_osv: {
        children: {
          android_osv_selection,
          android_osv,
        },
      },
      ios_osv: {
        children: {
          ios_osv_selection,
          ios_osv,
        },
      },
      device_types,
      device_brands: {
        children: {
          device_brands_selection,
          device_brands,
        },
      },
      launch_price: {
        children: {
          launch_price_selection,
          launch_price,
        },
      },
      network_types,
      hide_if_converted: {
        children: {
          hide_if_converted,
          converted_time_duration,
        },
      },
      auto_extend_targets: {
        children: {
          auto_extend_targets_selection,
          auto_extend_targets,
        },
      },
    } = targeting_form.config;

    // 地域选项
    geo_type.on = {
      change: () => {
        const is_location = geo_type.value === 'LOCATION';
        const is_residence = location_type.value === 'RESIDENCE';
        const is_current = location_type.value === 'CURRENT';
        location_type.hidden = !is_location;
        residence_geo_locations.hidden = !(is_location && is_residence);
        current_geo_locations.hidden = !(is_location && is_current);

        // 智能扩量
        auto_extend_targets.options.forEach(opt => {
          opt.value === 'REGION' && (opt.disabled = !is_location);
        });
        !is_location && (auto_extend_targets.value = auto_extend_targets.value.filter(val => val !== 'REGION'));
      },
    };

    // 性别
    gender.on = {
      change() {
        const is_unlimited = gender.value === 'NONE';
        // 智能扩量
        auto_extend_targets.options.forEach(opt => {
          opt.value === 'GENDER' && (opt.disabled = is_unlimited);
        });
        is_unlimited && (auto_extend_targets.value = auto_extend_targets.value.filter(val => val !== 'GENDER'));
      },
    };

    // 人群类型
    audience_types.on = {
      change() {
        audience_types.value = checkboxFilterNone(audience_types.value, 'INFINITE');
        const is_include_audience = audience_types.value.includes('INCLUDE');
        const is_exclude_audience = audience_types.value.includes('EXCLUDE');
        include_audiences.hidden = !is_include_audience;
        exclude_audiences.hidden = !is_exclude_audience;

        const is_unlimited = audience_types.value.includes('INFINITE');

        // 智能扩量
        auto_extend_targets.options.forEach(opt => {
          opt.value === 'CUSTOM_AUDIENCE' && (opt.disabled = is_unlimited);
        });
        is_unlimited && (auto_extend_targets.value = auto_extend_targets.value.filter(val => val !== 'CUSTOM_AUDIENCE'));
      },
    };

    // 年龄
    ages_selection.on = {
      change: () => {
        const is_custom = ages_selection.value === OPTIONS_TYPE.CUSTOM;
        ages.hidden = !is_custom;

        // 智能扩量
        auto_extend_targets.options.forEach(opt => {
          opt.value === 'AGE' && (opt.disabled = !is_custom);
        });
        !is_custom && (auto_extend_targets.value = auto_extend_targets.value.filter(val => val !== 'AGE'));
      },
    };

    // 行为兴趣
    interest_action_mode.on = {
      change: () => {
        const is_custom = interest_action_mode.value === 'CUSTOM';
        action_days.hidden = !is_custom;
        action_categories.hidden = !is_custom;
        interest_categories.hidden = !is_custom;
      },
    };

    // 抖音达人
    aweme_fan_selection.on = {
      change: () => {
        aweme_fan_selection.value = checkboxFilterNone(aweme_fan_selection.value, OPTIONS_TYPE.UNLIMITED);
        const is_aweme_fan_categories = aweme_fan_selection.value.includes('aweme_fan_categories');
        const is_aweme_fan_accounts = aweme_fan_selection.value.includes('aweme_fan_accounts');
        aweme_fan_behaviors.hidden = !(is_aweme_fan_categories || is_aweme_fan_accounts);
        aweme_fan_time_scope.hidden = !(is_aweme_fan_categories || is_aweme_fan_accounts);
        aweme_fan_categories.hidden = !is_aweme_fan_categories;
        aweme_fan_accounts.hidden = !is_aweme_fan_accounts;
      },
    };

    // 穿山甲媒体定向
    superior_popularity_type.on = {
      change: () => {
        const is_flow_package = superior_popularity_type.value === 'flow_package';
        const is_exclude_flow_package = superior_popularity_type.value === 'exclude_flow_package';
        flow_packages.hidden = !is_flow_package;
        exclude_flow_packages.hidden = !is_exclude_flow_package;
      },
    };

    // 系统平台
    platforms.on = {
      change: () => {
        platforms.value = checkboxFilterNone(platforms.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 安卓版本
    android_osv_selection.on = {
      change: () => {
        const is_custom = android_osv_selection.value === OPTIONS_TYPE.CUSTOM;
        android_osv.hidden = !is_custom;
      },
    };

    // IOS版本
    ios_osv_selection.on = {
      change: () => {
        const is_custom = ios_osv_selection.value === OPTIONS_TYPE.CUSTOM;
        ios_osv.hidden = !is_custom;
      },
    };

    // 设备类型
    device_types.on = {
      change: () => {
        device_types.value = checkboxFilterNone(device_types.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 设备品牌
    device_brands_selection.on = {
      change: () => {
        const is_custom = device_brands_selection.value === OPTIONS_TYPE.CUSTOM;
        device_brands.hidden = !is_custom;
      },
    };

    // 设备价格
    launch_price_selection.on = {
      change: () => {
        const is_custom = launch_price_selection.value === OPTIONS_TYPE.CUSTOM;
        launch_price.hidden = !is_custom;
      },
    };

    // 网络环境
    network_types.on = {
      change: () => {
        network_types.value = checkboxFilterNone(network_types.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 过滤已转化用户
    hide_if_converted.on = {
      change: () => {
        const is_custom = ['CUSTOMER', 'ORGANIZATION', 'APP'].includes(hide_if_converted.value);
        converted_time_duration.hidden = !is_custom;
      },
    };

    // 智能扩量
    auto_extend_targets_selection.on = {
      change: () => {
        const is_custom = auto_extend_targets_selection.value === OPTIONS_TYPE.CUSTOM;
        auto_extend_targets.hidden = !is_custom;
      },
    };
  }

  /**
   * 获取覆盖字符对象
   * @returns {Object}
   */
  getDifferentiatedOverrides() {
    // 营销场景
    const marketing_goal = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_DELIVERY_FORM_KEY, 'marketing_goal']);
    // 抖音号ID - 非星广联投
    const dou_yin_ids = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'dou_yin_ids']);
    // 排除指定抖音号 - 星广联投
    const exclude_aweme_ids = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'exclude_aweme_ids']);
    // 指定抖音号 - 星广联投
    const aweme_ids = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'aweme_ids']);
    // 小游戏链接
    const byte_dance_games = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'byte_dance_games']);
    // 达人投稿视频
    const star_task_author_videos = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'star_task_author_videos']);
    // 产品主图
    const image_ids = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'product_info.image_ids']);
    // 原生锚点
    const anchor_id = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'anchor_id']);
    // 关联星广联投
    const star_tasks = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_DELIVERY_FORM_KEY, 'star_tasks']);

    return {
      [marketing_goal]: ({ field_config }) => {
        field_config.formItemTips.content = '';
      },
      [dou_yin_ids]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [exclude_aweme_ids]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [aweme_ids]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [byte_dance_games]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [star_task_author_videos]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [image_ids]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [anchor_id]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [star_tasks]: ({ field_config }) => {
        field_config.isPreview = true;
      },
    };
  }

  /**
   * 设置配置模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setFieldDataSources(config, controller) {
    try {
      await super.setFieldDataSources(config, controller);
      const {
        campaign_config: {
          config: {
            site_form,
          },
        },
        creative_config: {
          config: {
            creative_components,
          },
        },
      } = config;
      const { site_sets } = site_form.config;
      const { yuntu_category_id, cdp_brand_id } = creative_components.config;
      site_sets.options = site_sets.options.map(opt => ({ ...opt, disabled: false }));
      cdp_brand_id.options = await controller.getBrandList();
      const [cdp_brand_id_value, cdp_brand_name_value] = String(cdp_brand_id.value || '').split('#');
      const selected_brand = cdp_brand_id.options.find(({ value, jrtt_v3_params = {}}) => {
        return value === cdp_brand_id.value ||
          (String(jrtt_v3_params.cdp_brand_id) === cdp_brand_id_value && jrtt_v3_params.cdp_brand_name === cdp_brand_name_value);
      });
      const outer_brand_id = selected_brand?.jrtt_v3_params?.brand_name_id;
      yuntu_category_id.options = outer_brand_id ? await controller.getBrandCategoryList({ outer_brand_id }) : [];
    } catch (error) {
      console.error(error);
      throw new Error('JrttV3FieldStrategy: setFieldDataSources fail');
    }
  }

  /**
   * 设置定向模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setTargetingDataSources(config, controller) {
    await super.setTargetingDataSources(config, controller);
    const {
      gender,
      ages: {
        children: {
          ages,
        },
      },
      interest_action: {
        children: {
          action_days,
          action_categories,
          interest_categories,
        },
      },
      aweme_fan: {
        children: {
          aweme_fan_behaviors,
          aweme_fan_time_scope,
          aweme_fan_categories,
          aweme_fan_accounts,
        },
      },
      superior_popularity_type: {
        children: {
          flow_packages,
          exclude_flow_packages,
        },
      },
      android_osv: {
        children: {
          android_osv,
        },
      },
      ios_osv: {
        children: {
          ios_osv,
        },
      },
      device_brands: {
        children: {
          device_brands,
        },
      },
      network_types,
    } = config.targeting_config.config.targeting_form.config;

    // 定向字典
    const targeting_dictionary = await controller.getTargetingDictionary(['age', 'action_days', 'action_categories', 'interest_categories', 'aweme_fan_behaviors', 'aweme_fan_time_scope', 'aweme_fan_categories', 'aweme_fan_accounts', 'flow_packages', 'device_brands', 'android_osv', 'ios_osv', 'network_types']);
    const unlimited_selection = { label: '不限', value: OPTIONS_TYPE.UNLIMITED };

    // 性别
    gender.options = targeting_dictionary?.gender || [];
    // 年龄
    ages.options = targeting_dictionary?.age || [];
    // 行为兴趣
    action_days.options = targeting_dictionary?.action_days || [];
    action_categories.options = targeting_dictionary?.action_categories || [];
    interest_categories.options = targeting_dictionary?.interest_categories || [];
    // 抖音达人
    aweme_fan_behaviors.options = targeting_dictionary?.aweme_fan_behaviors || [];
    aweme_fan_time_scope.options = targeting_dictionary?.aweme_fan_time_scope || [];
    aweme_fan_categories.options = targeting_dictionary?.aweme_fan_categories || [];
    aweme_fan_accounts.options = targeting_dictionary?.aweme_fan_accounts || [];
    // 穿山甲媒体定向
    flow_packages.options = targeting_dictionary?.flow_packages || [];
    exclude_flow_packages.options = targeting_dictionary?.flow_packages || [];
    // 安卓版本
    android_osv.options = targeting_dictionary?.android_osv || [];
    // IOS版本
    ios_osv.options = targeting_dictionary?.ios_osv || [];
    // 设备品牌
    device_brands.options = targeting_dictionary?.device_brands || [];
    // 网络环境
    network_types.options = [unlimited_selection, ...(targeting_dictionary?.network_types || [])];
  }

  /**
   * 模块字段排序
   * @param {Object} fields
   * @returns {Object}
   */
  async setFieldsSort(fields) {
    const { campaign_config } = fields;
    const {
      daily_budget_type,
      daily_budget,
      price_type,
      price,
      price_strategy,
      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
      deep_bid_type,
      cpa_bid,
      roi_goal,
      ...rest
    } = campaign_config.config.budget_and_bid_form.config;
    campaign_config.config.budget_and_bid_form.config = {
      daily_budget_type,
      daily_budget,
      price_type,
      price,
      price_strategy,
      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
      deep_bid_type,
      cpa_bid,
      roi_goal,
      ...rest,
    };
    return Promise.resolve(fields);
  }

  /**
   * 设置动态渲染组件
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   * @param {Object} payload 传递的数据，不限制键名和值
   */
  async setDynamicComponent(config, controller, payload) {
    if (config && controller && payload) {
      const { raw_ref_form_model, raw_form_model, form_assign_data } = payload;
      const is_channel_codes_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.fusion__channel_code?.fusion__channel_codes) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.fusion__channel_code?.fusion__channel_codes);
      const is_delivery_mode_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.delivery_mode) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.delivery_mode);
      const is_delivery_platform_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.delivery_platform_type);
      const is_delivery_content_id_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.delivery_content_id) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.delivery_content_id);
      const is_marketing_goal_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.marketing_goal) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.marketing_goal);
      const is_native_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.native_type) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.native_type);
      const is_star_tasks_selection_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.star_task_switch) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.star_task_switch);
      const is_price_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.budget_and_bid_form']?.price_type) !== JSON.stringify(raw_form_model['campaign_config.budget_and_bid_form']?.price_type);
      const is_price_strategy_changed = JSON.stringify(raw_ref_form_model['campaign_config.budget_and_bid_form']?.price_strategy) !== JSON.stringify(raw_form_model['campaign_config.budget_and_bid_form']?.price_strategy);
      const is_deep_convert_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.budget_and_bid_form']?.deep_convert_type) !== JSON.stringify(raw_form_model['campaign_config.budget_and_bid_form']?.deep_convert_type);
      const is_deep_bid_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.budget_and_bid_form']?.deep_bid_type) !== JSON.stringify(raw_form_model['campaign_config.budget_and_bid_form']?.deep_bid_type);

      const is_valid = [
        is_channel_codes_changed,
        is_delivery_mode_changed,
        is_delivery_platform_type_changed,
        is_delivery_content_id_changed,
        is_marketing_goal_changed,
        is_native_type_changed,
        is_star_tasks_selection_changed,
        is_price_type_changed,
        is_price_strategy_changed,
        is_deep_convert_type_changed,
        is_deep_bid_type_changed].some(e => e);

      if (is_valid) {
        delete form_assign_data.creative_config;
        await this.renderDynamicComponent({ config, payload, controller });
      }
    }
  }

  /**
   * 渲染创意动态组件
   * @param {Object} data 配置项
   * @param {Object} data.config 模块表单配置
   * @param {Object} payload 传递的数据，不限制键名和值
   */
  async renderDynamicComponent({ config, payload, controller }) {
    this.addEvent(config, controller);
    const {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              native_type,
            },
          },
        },
      },
      creative_config: {
        config: {
          content_form: {
            config: {
              materials_type,
              dou_yin_ids,
              aweme_setting_type,
              original_video_title,
              byte_dance_games,
            },
          },
          creative_components: {
            config: {
              source,
              star_task_author_videos,
              star_task_author_videos_num_per_creative,
              anchor_related_type,
              anchor_id,
            },
          },
          creative_price: {
            config: {
              cpa_bid,
              roi_goal,
            },
          },
        },
      },
    } = config;

    const { raw_ref_form_model, raw_form_model } = payload;
    const is_channel_codes_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.fusion__channel_code?.fusion__channel_codes) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.fusion__channel_code?.fusion__channel_codes);
    const is_delivery_content_id_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.delivery_content_id) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.delivery_content_id);
    const is_live = raw_ref_form_model['campaign_config.delivery_form']?.marketing_goal === 'LIVE';
    const is_procedural = raw_ref_form_model['campaign_config.delivery_form']?.delivery_mode === 'PROCEDURAL';
    const is_manual = raw_ref_form_model['campaign_config.delivery_form']?.delivery_mode === 'MANUAL';
    const is_android = raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type === 'ANDROID';
    const is_harmony = raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type === 'HARMONY';
    const is_ios = raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type === 'IOS';
    const is_micro_game = raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type === 'MICRO_GAME';
    const is_star_tasks_on = raw_ref_form_model['campaign_config.delivery_form']?.star_task_switch === 'ON';
    const is_native_type_forced = is_star_tasks_on || is_live;
    const is_aweme = is_native_type_forced || raw_ref_form_model['campaign_config.delivery_form']?.native_type === 'AWEME';
    const is_ocpx = raw_ref_form_model['campaign_config.budget_and_bid_form']?.price_type === 'PRICING_OCPM';
    const is_custom = raw_ref_form_model['campaign_config.budget_and_bid_form']?.price_strategy === 'CUSTOM';
    const is_has_deep_convert_type = raw_ref_form_model['campaign_config.budget_and_bid_form']?.deep_convert_type;

    const mapTuple = [
      // 原生身份
      () => {
        is_native_type_forced && (native_type.value = 'AWEME');
        native_type.props.disabled = is_native_type_forced;
      },
      // 素材类型
      () => {
        materials_type.hidden = !is_live;
        materials_type.on.change();
      },
      // 达人投稿视频筛选
      () => {
        dou_yin_ids.hidden = !(!is_star_tasks_on && is_aweme);
        aweme_setting_type.hidden = !(is_star_tasks_on && is_aweme);
        aweme_setting_type.on.change();
        aweme_setting_type.props.disabled = false;
      },
      // 使用原视频标题
      () => {
        /**
         * 自动优选素材为【启用(ON)】时
         * - 使用原视频标题默认选中【启用(ON)】
         * - 使用原视频标题的【不启用(OFF)】选项置灰不可选取
         */
        original_video_title.props.disabled = false;

        original_video_title.hidden = !is_star_tasks_on;
      },
      // 小游戏链接
      () => {
        byte_dance_games.hidden = !is_micro_game;
        (is_channel_codes_changed || is_delivery_content_id_changed) && (byte_dance_games.value.result_value = []);
      },
      // 达人投稿视频
      () => {
        star_task_author_videos.hidden = !is_star_tasks_on;
      },
      // 每创意分配投稿视频数
      () => {
        star_task_author_videos_num_per_creative.hidden = !is_star_tasks_on;
        /**
         * - 投放方式为自动投放流程下最大选取数量：30
         * - 投放方式为手动投放流程下最大选取数量：10
         */
        const sum = is_procedural ? 30 : 10;
        star_task_author_videos_num_per_creative.value = Math.min(star_task_author_videos_num_per_creative.value, sum);
        star_task_author_videos_num_per_creative.options = getNumberOptions(1, sum);
        star_task_author_videos_num_per_creative.on.change();
      },
      // 广告来源
      () => {
        source.rules.required = !(is_android || is_ios || is_harmony);
      },
      // 原生锚点
      () => {
        anchor_related_type.props.disabled = is_micro_game;
        is_micro_game && (anchor_related_type.value = 'OFF');
        anchor_related_type.hidden = !(is_aweme && (materials_type.hidden || (!materials_type.hidden && materials_type.value === 'PROMOTION_MATERIALS')));
        anchor_related_type?.on?.change?.();
        is_channel_codes_changed && (anchor_id.value.result_value = []);
      },
      // 创意出价 - 优化目标出价
      () => {
        cpa_bid.hidden = !(is_manual && is_ocpx && is_custom);
      },
      // 创意出价 - 深度ROI系数出价
      () => {
        const is_roi_coefficient = raw_ref_form_model['campaign_config.budget_and_bid_form']?.deep_bid_type === 'ROI_COEFFICIENT';
        roi_goal.hidden = !(is_manual && is_ocpx && is_custom && is_has_deep_convert_type && is_roi_coefficient);
      },
    ];

    mapTuple.forEach(handler => handler());
  }

  /**
   * 转换预览参数
   * @param {Object} data 数据
   * @returns {Object}
   */
  transformPreviewPayload(data) {
    const payload = super.transformPreviewPayload(data);
    const { delivery_platform_type } = data.form_assign_data.campaign_config?.delivery_form || {};
    const { price_type } = data.form_assign_data.campaign_config?.budget_and_bid_form || {};
    if (isHarmonyCpcOrCpm(delivery_platform_type, price_type)) {
      delete payload.campaign?.price;
    }
    return payload;
  }

  /**
   * 转换提交参数
   * @param {Object} data 数据
   * @returns {Object}
   */
  transformSubmitPayload(data) {
    const payload = super.transformSubmitPayload(data);
    payload.batch_params.forEach(({ plans }) => {
      plans.forEach(({ campaign }) => {
        if (isHarmonyCpcOrCpm(campaign.delivery_platform_type, campaign.price_type)) {
          delete campaign.price;
        }
      });
    });
    return payload;
  }

  /**
   * 转换差异化广告预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   */
  transformDifferentiatedCampaignPreviewPayload({ form_assign_data }) {
    const {
      campaign_config: {
        schedule_form: {
          time_period_type,
          schedule_time,
          time_period,
        },
        delivery_form: {
          marketing_goal,
          audience_type,
          native_type,
          app_promotion_type,
          download_type,
          download_mode,
          delivery_mode,
          star_task_switch,
          star_tasks = {},
        },
        budget_and_bid_form: {
          price_type,
          fusion__optimize_goal,
          deep_convert_type,
          deep_bid_type,
          cpa_bid,
          roi_goal,
        },
      },
    } = form_assign_data;

    /**
     * CPC：固定为点击量
     * CPM：固定为展示量
     * oCPX：正常取值优化目标
     */
    const convert_goal_map = {
      'PRICING_CPC': 'AD_CONVERT_TYPE_CLICK_NUM',
      'PRICING_CPM': 'AD_CONVERT_TYPE_SHOW_OFF_NUM',
      'PRICING_OCPM': fusion__optimize_goal,
    };

    // 关联星广联投
    const star_tasks_value = Object.entries(star_tasks).map(([advertiser_id, list]) => {
      return {
        advertiser_id,
        star_task_id: list[0].id,
      };
    });
    const is_native_type_forced = star_task_switch === 'ON' || marketing_goal === 'LIVE';

    return {
      // 投放时间 - 全天
      ...(time_period_type === 'TIME_PERIOD_ALL' && { time_period: new Array(TIME_PERIOD_LENGTH.HALF_HOUR).fill(1).join('') }),
      // 投放时间 - 指定时间
      ...(time_period_type === 'TIME_PERIOD_DAY_SPECIFIC' && { time_period: transformSelectedTimeToTimeSeries(schedule_time.start, schedule_time.end) }),
      // 投放时间 - 指定多个时间
      ...(time_period_type === 'TIME_PERIOD_HOUR_SPECIFIC' && { time_period: time_period.schedule_time_content }),
      // 营销场景
      ...(marketing_goal && { marketing_goal }),
      // 定向类型
      ...(audience_type && { audience_type }),
      // 原生身份
      ...(native_type && { native_type: is_native_type_forced ? 'AWEME' : native_type }),
      // 投放目的
      ...(app_promotion_type && { app_promotion_type }),
      // 下载方式
      ...(download_type && { download_type }),
      // 优先应用商店下载
      ...(download_mode && { download_mode }),
      // 投放方式
      ...(delivery_mode && { delivery_mode }),
      // 关联星广联投
      ...(star_tasks_value.length && { star_tasks: star_tasks_value }),
      // 优化目标
      convert_goal: convert_goal_map[price_type],
      // 深度优化目标
      ...(deep_convert_type && { deep_convert_goal: deep_convert_type }),
      // 深度优化方式
      ...(deep_bid_type && { deep_bid_type }),
      // 优化目标出价
      ...(cpa_bid && { cpa_bid: calculator.times(cpa_bid, 100) }),
      // 深度ROI系数出价
      ...(roi_goal && { roi_goal }),
    };
  }

  /**
   * 转换差异化创意预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   * @param {Object} data.extra_config 配置数据
   * @returns {Object}
   */
  transformDifferentiatedCreativePreviewPayload({ form_assign_data }) {
    const {
      campaign_config: {
        delivery_form: {
          marketing_goal,
          native_type,
          star_task_switch,
        } = {},
      } = {},
      creative_config: {
        base_form: {
          operation,
        },
        content_form: {
          delivery_mode,
          materials_type,
          images_num_per_creative,
          videos_num_per_creative,
          titles_num_per_creative,
          sites_num_per_creative,
          dou_yin_ids = {},
          original_video_title,
          byte_dance_games: {
            result_value: byte_dance_games_value = [],
          } = {},
        } = {},
        creative_components: {
          product_info: {
            titles,
            image_ids,
            selling_points,
          },
          call_to_action_buttons,
          source,
          star_task_author_videos = {},
          star_task_author_videos_num_per_creative,
          anchor_related_type,
          anchor_id: {
            result_value: anchor_id_result_value = [],
          } = {},
          yuntu_category_id,
          cdp_brand_id,
        } = {},
        creative_price: {
          cpa_bid,
          roi_goal,
        } = {},
      },
    } = form_assign_data;

    // 分配规则
    const extra_dispatch_rules = {
      ...(images_num_per_creative && { images_num_per_creative }),
      ...(videos_num_per_creative && { videos_num_per_creative }),
      ...(titles_num_per_creative && { titles_num_per_creative }),
      ...(sites_num_per_creative && { sites_num_per_creative }),
      // 每创意分配投稿视频数
      ...(star_task_author_videos_num_per_creative && { star_task_author_videos_num_per_creative }),
    };
    const is_native_type_forced = star_task_switch === 'ON' || marketing_goal === 'LIVE';
    const actual_native_type = is_native_type_forced ? 'AWEME' : native_type;
    // 原生设置 - 非关联星广联投
    const native_settings = Object.entries(dou_yin_ids).map(([advertiser_id, list]) => {
      return {
        advertiser_id,
        dou_yin_ids: list.map(({ id }) => id),
      };
    });
    // 达人投稿视频
    const star_task_author_videos_value = Object.entries(star_task_author_videos).map(([advertiser_id, list]) => {
      return {
        advertiser_id,
        star_task_author_videos: list.map(({ video_id, video_cover_id, image_mode, item_id }) => ({ video_id, video_cover_id, image_mode, item_id })),
      };
    });
    // 原生锚点
    const anchor_material_list = anchor_id_result_value.map(({ advertiser_id, channel_alias, type, value }) => ({ advertiser_id, channel_alias, anchor_id: value, anchor_type: type }));
    // 小游戏链接
    const byte_dance_games = byte_dance_games_value.map(({ advertiser_id, channel_alias, value }) => ({ advertiser_id, channel_alias, url: value.split('#')[1] }));
    // 产品信息
    const product_info = {
      ...(titles && { titles: [titles] }),
      ...(image_ids?.length && { image_ids: image_ids.map(({ id }) => id) }),
      ...(selling_points && { selling_points }),
    };
    const [cdp_brand_id_value, cdp_brand_name_value] = String(cdp_brand_id || '').split('#');
    const brand_options = this.config?.creative_config?.config?.creative_components?.config?.cdp_brand_id?.options || [];
    const selected_brand = brand_options.find(({ value, jrtt_v3_params = {}}) => {
      return value === cdp_brand_id ||
        (String(jrtt_v3_params.cdp_brand_id) === cdp_brand_id_value && jrtt_v3_params.cdp_brand_name === cdp_brand_name_value);
    });
    const brand_params = selected_brand?.jrtt_v3_params || {};
    const has_sub_brand_names = Object.prototype.hasOwnProperty.call(brand_params, 'sub_brand_names');
    const has_sub_brand_name_ids = Object.prototype.hasOwnProperty.call(brand_params, 'sub_brand_name_ids');
    // 品牌信息
    const brand_info = {
      ...(yuntu_category_id && { yuntu_category_id: Number(yuntu_category_id) || yuntu_category_id }),
      ...(cdp_brand_id && {
        cdp_brand_id: Number(brand_params.cdp_brand_id || cdp_brand_id_value) || brand_params.cdp_brand_id || cdp_brand_id_value,
        cdp_brand_name: brand_params.cdp_brand_name || cdp_brand_name_value,
      }),
      ...(brand_params.brand_name_id && { brand_name_id: brand_params.brand_name_id }),
      ...(has_sub_brand_names && { sub_brand_names: brand_params.sub_brand_names || [] }),
      ...(has_sub_brand_name_ids && { sub_brand_name_ids: brand_params.sub_brand_name_ids || [] }),
    };

    return {
      // 创意状态
      operation,
      // 投放模式
      ...(delivery_mode && { delivery_mode }),
      // 素材类型
      ...(materials_type && { materials_type }),
      // 分配规则
      ...(Object.keys(extra_dispatch_rules).length && { extra_dispatch_rules }),
      // 使用原视频标题
      ...(original_video_title && { original_video_title }),
      // 小游戏链接
      ...(byte_dance_games.length && { byte_dance_games }),
      // 产品信息
      ...(Object.keys(product_info).length && { product_info }),
      // 行动号召
      ...(call_to_action_buttons && { call_to_action_buttons }),
      // 广告来源
      ...(source && { source }),
      // 达人投稿视频
      ...(star_task_author_videos_value.length && { star_task_author_videos: star_task_author_videos_value }),
      // 原生设置
      ...(actual_native_type === 'AWEME' && star_task_switch !== 'ON' && native_settings.length && { native_settings }),
      // 原生锚点启用开关
      ...(anchor_related_type && { anchor_related_type }),
      // 原生锚点
      ...(anchor_material_list.length && { anchor_material_list }),
      // 品牌信息
      ...(Object.keys(brand_info).length && { brand_info }),
      // 创意出价 - 优化目标出价
      ...(cpa_bid && { cpa_bid: calculator.times(cpa_bid, 100) }),
      // 创意出价 - 深度ROI系数出价
      ...(roi_goal && { roi_goal }),
    };
  }

  /**
   * 获取创建定向包差异化接口参数
   * @param {Object} target_data 定向数据
   * @returns {Object}
   */
  getDifferentiatedCreateTargetParams(target_data) {
    const {
      ages: {
        ages,
      },
      interest_action: {
        interest_action_mode,
        action_days,
        action_categories,
        interest_categories,
      },
      aweme_fan: {
        aweme_fan_behaviors,
        aweme_fan_time_scope,
        aweme_fan_categories,
        aweme_fan_accounts,
      },
      superior_popularity_type: {
        superior_popularity_type,
        flow_packages,
        exclude_flow_packages,
      },
      platforms,
      android_osv: {
        android_osv,
      },
      ios_osv: {
        ios_osv,
      },
      device_types,
      device_brands: {
        device_brands,
      },
      launch_price: {
        launch_price,
      },
      network_types,
      hide_if_exists,
      hide_if_converted: {
        hide_if_converted,
        converted_time_duration,
      },
      auto_extend_targets: {
        auto_extend_targets,
      },
    } = target_data;

    const target_params = {
      [`${CHANNEL_INTERFACE_TYPE.JRTT_V3}_params`]: {
        // 年龄
        ...(ages && { ages }),
        // 行为兴趣
        ...(interest_action_mode && { interest_action_mode }),
        // 用户行为天数
        ...(action_days && { action_days }),
        // 行为类目
        ...(action_categories && { action_categories }),
        // 兴趣类目
        ...(interest_categories && { interest_categories }),
        // 互动行为类型
        ...(aweme_fan_behaviors && { aweme_fan_behaviors }),
        // 互动行为天数
        ...(aweme_fan_time_scope && { aweme_fan_time_scope }),
        // 抖音达人分类
        ...(aweme_fan_categories && { aweme_fan_categories }),
        // 抖音达人
        ...(aweme_fan_accounts && { aweme_fan_accounts }),
        // 媒体定向
        ...(superior_popularity_type !== OPTIONS_TYPE.UNLIMITED && { superior_popularity_type }),
        // 定向媒体包
        ...(flow_packages && { flow_packages }),
        // 排除媒体包
        ...(exclude_flow_packages && { exclude_flow_packages }),
        // 系统平台
        ...(!platforms.includes(OPTIONS_TYPE.UNLIMITED) && { platforms }),
        // 安卓版本
        ...(android_osv && { android_osv }),
        // IOS版本
        ...(ios_osv && { ios_osv }),
        // 设备类型
        ...(!device_types.includes(OPTIONS_TYPE.UNLIMITED) && { device_types }),
        // 设备品牌
        ...(device_brands && { device_brands }),
        // 设备价格
        ...(launch_price && { launch_price: [launch_price.start, launch_price.end] }),
        // 网络环境
        ...(!network_types.includes(OPTIONS_TYPE.UNLIMITED) && { network_types }),
        // 已安装用户
        ...(hide_if_exists && { hide_if_exists }),
        // 过滤已转化用户
        ...(hide_if_converted && { hide_if_converted }),
        // 过滤时间范围
        ...(converted_time_duration && { converted_time_duration }),
        // 智能扩量
        ...(auto_extend_targets && { auto_extend_targets }),
      },
    };

    return target_params;
  }

  /**
   * 获取定向包差异化详情表单数据
   * @param {Object} target_params 定向详情数据
   * @returns {Object}
   */
  getDifferentiatedTargetDetailFormData(target_params) {
    const {
      [`${CHANNEL_INTERFACE_TYPE.JRTT_V3}_params`]: {
        ages,
        interest_action_mode,
        action_days,
        action_categories,
        interest_categories,
        aweme_fan_behaviors,
        aweme_fan_time_scope,
        aweme_fan_categories,
        aweme_fan_accounts,
        superior_popularity_type,
        flow_packages,
        exclude_flow_packages,
        platforms,
        android_osv,
        ios_osv,
        device_types,
        device_brands,
        launch_price,
        network_types,
        hide_if_exists,
        hide_if_converted,
        converted_time_duration,
        auto_extend_targets,
      } = {},
    } = target_params;

    const form_data = {
      // 年龄
      ...(ages && {
        ages: {
          ages_selection: OPTIONS_TYPE.CUSTOM,
          ages,
        },
      }),
      // 行为兴趣
      ...(interest_action_mode && {
        interest_action: {
          interest_action_mode,
          action_days,
          action_categories,
          interest_categories,
        },
      }),
      // 抖音达人
      ...(aweme_fan_behaviors && {
        aweme_fan: {
          aweme_fan_selection: [...(aweme_fan_categories ? ['aweme_fan_categories'] : []), ...(aweme_fan_accounts ? ['aweme_fan_accounts'] : [])],
          aweme_fan_behaviors,
          aweme_fan_time_scope,
          ...(aweme_fan_categories && { aweme_fan_categories }),
          ...(aweme_fan_accounts && { aweme_fan_accounts }),
        },
      }),
      // 穿山甲媒体定向
      ...(superior_popularity_type && {
        superior_popularity_type: {
          superior_popularity_type,
          ...(flow_packages && { flow_packages }),
          ...(exclude_flow_packages && { exclude_flow_packages }),
        },
      }),
      // 系统平台
      ...(platforms && { platforms }),
      // 安卓版本
      ...(android_osv && {
        android_osv: {
          android_osv_selection: OPTIONS_TYPE.CUSTOM,
          android_osv,
        },
      }),
      // IOS版本
      ...(ios_osv && {
        ios_osv: {
          ios_osv_selection: OPTIONS_TYPE.CUSTOM,
          ios_osv,
        },
      }),
      // 设备类型
      ...(device_types && { device_types }),
      // 设备品牌
      ...(device_brands && {
        device_brands: {
          device_brands_selection: OPTIONS_TYPE.CUSTOM,
          device_brands,
        },
      }),
      // 设备价格
      ...(launch_price && {
        launch_price: {
          launch_price_selection: OPTIONS_TYPE.CUSTOM,
          launch_price: { start: launch_price[0], end: launch_price[1] },
        },
      }),
      // 网络环境
      ...(network_types && { network_types }),
      // 已安装用户
      ...(hide_if_exists && { hide_if_exists }),
      // 过滤已转化用户
      ...(hide_if_converted && {
        hide_if_converted: {
          hide_if_converted,
          ...(converted_time_duration && { converted_time_duration }),
        },
      }),
      // 智能扩量
      ...(auto_extend_targets && {
        auto_extend_targets: {
          auto_extend_targets_selection: OPTIONS_TYPE.CUSTOM,
          auto_extend_targets,
        },
      }),
    };

    return form_data;
  }

  /**
   * 获取定向数据转预览数据关联表
   * @returns {Object}
   */
  getTargetPreviewAssociatedMap() {
    return {
      ages_selection: {
        associated_keys: ['ages'],
      },
      android_osv_selection: {
        associated_keys: ['android_osv'],
      },
      ios_osv_selection: {
        associated_keys: ['ios_osv'],
      },
      device_brands_selection: {
        associated_keys: ['device_brands'],
      },
      launch_price_selection: {
        associated_keys: ['launch_price'],
      },
      auto_extend_targets_selection: {
        associated_keys: ['auto_extend_targets'],
      },
    };
  }

  /**
   * 获取差异化模板配置表
   * @returns {Object}
   */
  getDifferentiatedTemplateMap() {
    return {
      campaign_config: {
        delivery_form: ['marketing_goal', 'audience_type', 'native_type', 'app_promotion_type', 'download_type', 'download_mode', 'delivery_mode', 'star_task_switch'],
      },
      creative_config: {
        base_form: ['operation'],
        content_form: ['delivery_mode', 'materials_type', 'videos_num_per_creative', 'images_num_per_creative', 'titles_num_per_creative', 'sites_num_per_creative', 'dou_yin_ids', 'aweme_setting_type', 'original_video_title'],
        creative_components: ['product_info', 'call_to_action_buttons', 'source', 'anchor_related_type'],
        creative_price: ['cpa_bid'],
      },
    };
  }

  /**
   * 是否重新渲染动态创意
   * @param {Object} old_campaign_config 旧广告配置
   * @param {Object} new_campaign_config 新广告配置
   * @returns {Boolean}
   */
  isReRenderCreative(old_campaign_config, new_campaign_config) {
    const {
      delivery_form: {
        config: {
          fusion__channel_code: {
            children: {
              fusion__channel_codes: old_fusion__channel_codes,
            },
          },
          delivery_platform_type: old_delivery_platform_type,
          delivery_mode: old_delivery_mode,
          delivery_content_id: old_delivery_content_id,
        },
      },
      budget_and_bid_form: {
        config: {
          price_type: old_price_type,
          price_strategy: old_price_strategy,
          deep_convert_type: old_deep_convert_type,
          deep_bid_type: old_deep_bid_type,
        },
      },
    } = old_campaign_config.config;

    const {
      delivery_form: {
        config: {
          fusion__channel_code: {
            children: {
              fusion__channel_codes: new_fusion__channel_codes,
            },
          },
          delivery_platform_type: new_delivery_platform_type,
          delivery_mode: new_delivery_mode,
          delivery_content_id: new_delivery_content_id,
        },
      },
      budget_and_bid_form: {
        config: {
          price_type: new_price_type,
          price_strategy: new_price_strategy,
          deep_convert_type: new_deep_convert_type,
          deep_bid_type: new_deep_bid_type,
        },
      },
    } = new_campaign_config.config;

    const is_channel_codes_changed = JSON.stringify(old_fusion__channel_codes.value) !== JSON.stringify(new_fusion__channel_codes.value);
    const is_delivery_platform_type_changed = JSON.stringify(old_delivery_platform_type.value) !== JSON.stringify(new_delivery_platform_type.value);
    const is_delivery_mode_changed = JSON.stringify(old_delivery_mode.value) !== JSON.stringify(new_delivery_mode.value);
    const is_delivery_content_id_changed = JSON.stringify(old_delivery_content_id.value) !== JSON.stringify(new_delivery_content_id.value);
    const is_price_type_changed = JSON.stringify(old_price_type.value) !== JSON.stringify(new_price_type.value);
    const is_price_strategy_changed = JSON.stringify(old_price_strategy.value) !== JSON.stringify(new_price_strategy.value);
    const is_deep_convert_type_changed = JSON.stringify(old_deep_convert_type.value) !== JSON.stringify(new_deep_convert_type.value);
    const is_deep_bid_type_changed = JSON.stringify(old_deep_bid_type.value) !== JSON.stringify(new_deep_bid_type.value);
    const is_valid = [is_channel_codes_changed, is_delivery_platform_type_changed, is_delivery_mode_changed, is_delivery_content_id_changed, is_price_type_changed, is_price_strategy_changed, is_deep_convert_type_changed, is_deep_bid_type_changed].some(e => e);

    return is_valid;
  }

  /**
   * 是否显示预览按钮
   * @param {Object} form_assign_data 分配数据
   * @param {Object} form_fields 源数据
   * @param {Object} payload 配置参数
   * @returns {boolean}
   */
  isShowPreviewBtn(form_assign_data, form_fields, payload) {
    const { is_add_creative } = payload;
    const material = form_assign_data[MATERIAL_MODULE_KEY];
    const is_has_material = material?.images?.length > 0 || material?.videos?.length > 0;

    const {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              delivery_platform_type,
              marketing_goal,
              star_task_switch,
            },
          },
        },
      },
      creative_config: {
        config: {
          content_form: {
            config: {
              materials_type,
              original_video_title,
            },
          },
        },
      },
    } = form_fields;
    /**
     * 以下模块非必填的条件
     *
     * 广告信息
     * - 当前流程为添加创意
     *
     * 广告定向
     * - 当前流程为添加创意
     *
     * 视频/图片素材
     * - 关联星广联投为【启用(ON)】
     * - 素材类型为【直播素材['LIVE_MATERIALS']】
     *
     * 标题
     * - 使用原视频标题为【启用(ON)】
     * - 素材类型为【直播素材['LIVE_MATERIALS']】
     *
     * 落地页
     * - 投放平台为【IOS应用['IOS']】
     * - 素材类型为【直播素材['LIVE_MATERIALS']】 且 投放平台为应用类型
     * - 素材类型为【广告素材['PROMOTION_MATERIALS']】 且 营销场景为【直播['LIVE']】 且 投放平台为应用类型
     */
    const is_ios = delivery_platform_type.value === 'IOS';
    const is_app = ['ANDROID', 'IOS', 'HARMONY'].includes(delivery_platform_type.value);
    const is_live = marketing_goal.value === 'LIVE';
    const is_star_tasks_on = star_task_switch.value === 'ON';
    const is_promotion_materials = !materials_type.hidden && materials_type.value === 'PROMOTION_MATERIALS';
    const is_live_materials = !materials_type.hidden && materials_type.value === 'LIVE_MATERIALS';
    const is_original_video_title_on = !original_video_title.hidden && original_video_title.value === 'ON';

    const is_valid = [
      is_add_creative,
      is_app,
      is_live,
      is_star_tasks_on,
      is_live_materials,
      is_promotion_materials,
      is_original_video_title_on,
    ].some(v => v);

    if (is_valid) {
      const basic_keys = [
        ...(is_add_creative ? [] : [CAMPAIGN_MODULE_KEY]),
        CREATIVE_MODULE_KEY,
      ];
      const assign_keys = [
        ...(is_add_creative ? [] : [TARGETING_MODULE_KEY]),
        ...((is_original_video_title_on || is_live_materials) ? [] : [TITLE_MODULE_KEY]),
        ...((is_ios || (is_live_materials && is_app) || (is_promotion_materials && is_live && is_app)) ? [] : [SITE_MODULE_KEY]),
      ];
      const valid_material = [is_star_tasks_on, is_live_materials].some(v => v) || is_has_material;
      return basic_keys.every(key => !!form_assign_data[key]) && assign_keys.every(key => form_assign_data[key]?.length > 0) && valid_material;
    } else {
      return super.isShowPreviewBtn(form_assign_data, form_fields);
    }
  }

  /**
   * 设置源数据表单 - 广告
   * @param {Object} source 源数据
   * @param {Object} target 目标数据
   * @param {Object} form_assign_data 分配数据
   * @returns {void}
   */
  setSourceFormFieldsDataOfCampaign(source, target, form_assign_data) {
    super.setSourceFormFieldsDataOfCampaign(source, target, form_assign_data);
    const {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              star_task_switch,
            },
          },
        },
      },
      material_config: {
        config: {
          material_form: {
            config: {
              material,
            },
          },
        },
      },
    } = source;

    const map_tuple = [
      /**
       * 关联星广联投为【启用(ON)】时
       * - 清空素材数据
       */
      [
        () => star_task_switch.value === 'ON',
        () => {
          material.value.images?.splice?.(0);
          material.value.videos?.splice?.(0);
          delete form_assign_data.material_config;
        },
      ],
    ];

    map_tuple.forEach(([condition, handler]) => (condition() && handler()));
  }

  /**
   * 设置源数据表单
   * @param {Object} source 源数据
   * @param {Object} target 目标数据
   * @param {Object} form_assign_data 分配数据
   * @returns {void}
   */
  setSourceFormFieldsData(source, target, form_assign_data) {
    const {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              delivery_platform_type,
              marketing_goal,
            },
          },
        },
      },
      creative_config: {
        config: {
          content_form: {
            config: {
              materials_type,
              original_video_title,
            },
          },
        },
      },
      material_config: {
        config: {
          material_form: {
            config: {
              material,
            },
          },
        },
      },
      title_config: {
        selection_config: title_selection_config,
      },
      site_config: {
        selection_config: site_selection_config,
      },
    } = source;

    const map_tuple = [
      /**
       * 使用原视频标题为【启用(ON)】时
       * - 清空标题数据
       */
      [
        () => !original_video_title.hidden && original_video_title.value === 'ON',
        () => {
          title_selection_config.list.splice(0);
          form_assign_data.title_config?.splice(0);
          delete form_assign_data.title_config;
        },
      ],
      /**
       * 素材类型为【直播素材['LIVE_MATERIALS']】时
       * - 清空素材数据
       * - 清空标题数据
       *
       * 素材类型为【直播素材['LIVE_MATERIALS']】且投放平台为应用类型
       * 素材类型为【广告素材['PROMOTION_MATERIALS']】 且 营销场景为【直播['LIVE']】 且投放平台为应用类型
       * - 清空落地页数据
       */
      [
        () => !materials_type.hidden,
        () => {
          const is_app = ['ANDROID', 'IOS', 'HARMONY'].includes(delivery_platform_type.value);
          const is_live = marketing_goal.value === 'LIVE';
          const is_promotion_materials = materials_type.value === 'PROMOTION_MATERIALS';
          const is_live_materials = materials_type.value === 'LIVE_MATERIALS';

          if (is_live_materials) {
            material.value.images?.splice?.(0);
            material.value.videos?.splice?.(0);
            delete form_assign_data.material_config;

            title_selection_config.list.splice(0);
            form_assign_data.title_config?.splice(0);
            delete form_assign_data.title_config;

            if (is_app) {
              site_selection_config.list.splice(0);
              form_assign_data.site_config?.splice(0);
              delete form_assign_data.site_config;
            }
          }

          if (is_promotion_materials && is_live && is_app) {
            site_selection_config.list.splice(0);
            form_assign_data.site_config?.splice(0);
            delete form_assign_data.site_config;
          }
        },
      ],
    ];

    map_tuple.forEach(([condition, handler]) => (condition() && handler()));
  }
}
